import { Modal } from '../Modal';
import { formatMoeda, formatCpfCnpj, formatDataHoraSP } from '@fiodecobre/shared';
import type { NotaFiscalRow } from '../../modules/notas-fiscais/NotasFiscaisLista';

const STATUS_LABEL: Record<string, string> = {
  autorizada: 'Autorizada',
  cancelada: 'Cancelada',
  rejeitada: 'Rejeitada',
  erro_comunicacao: 'Erro de comunicacao',
};

interface FieldProps { label: string; children: React.ReactNode }

function Field({ label, children }: FieldProps) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 13, wordBreak: 'break-word' }}>{children || '—'}</div>
    </div>
  );
}

interface Props {
  nota: NotaFiscalRow;
  tipo: 'nfce' | 'nfe';
  onClose: () => void;
}

export function ModalDetalhesNota({ nota, tipo, onClose }: Props) {
  const label = tipo === 'nfce' ? 'NFC-e' : 'NF-e';
  return (
    <Modal aberto onFechar={onClose} titulo={`Detalhes da ${label}`} largura={720}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn">Fechar</button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Numero">{String(nota.numero).padStart(6, '0')} / Serie {nota.serie}</Field>
          <Field label="Ambiente">{String(nota.ambiente) === '1' ? 'Producao' : 'Homologacao'}</Field>
          <Field label="Status">{STATUS_LABEL[nota.status] || nota.status}</Field>
          <Field label="Protocolo">{nota.protocolo}</Field>
          <Field label="Emissao">{formatDataHoraSP(nota.emitido_em)}</Field>
          <Field label="Valor total">{formatMoeda(nota.valor_total)}</Field>
          {nota.cancelado_em && (
            <>
              <Field label="Cancelado em">{formatDataHoraSP(nota.cancelado_em)}</Field>
              <Field label="Motivo cancelamento">{nota.motivo_cancelamento || '—'}</Field>
            </>
          )}
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Chave de acesso">
            <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{nota.chave || '—'}</span>
          </Field>
          <Field label="Cliente">
            {nota.cliente_razao || nota.cliente_nome || (tipo === 'nfce' ? 'Consumidor' : '—')}
            {nota.cliente_doc && <span style={{ color: 'var(--text-muted)' }}> — {formatCpfCnpj(nota.cliente_doc)}</span>}
          </Field>
          {nota.orcamento_numero && <Field label="Orcamento">{nota.orcamento_numero}</Field>}
          {nota.vendedor_nome && <Field label="Vendedor">{nota.vendedor_nome}</Field>}
        </div>

        {(nota.codigo_status || nota.motivo_status) && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Resposta SEFAZ</div>
            <div style={{ fontSize: 13 }}>
              <strong>cStat {nota.codigo_status}</strong> — {nota.motivo_status}
            </div>
          </div>
        )}

        {/* [DA-488] A reconciliacao sobrescreve o motivo acima com a resposta da SEFAZ
            (quase sempre 217). Sem esta linha, a causa real da falha some do registro. */}
        {nota.erro_comunicacao_msg && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
              Falha de comunicacao na emissao
            </div>
            <div style={{ fontSize: 13 }}>{nota.erro_comunicacao_msg}</div>
          </div>
        )}

        {nota.qr_code_url && (
          <Field label="QR Code URL">
            <a href={nota.qr_code_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--primary)', wordBreak: 'break-all' }}>
              {nota.qr_code_url}
            </a>
          </Field>
        )}
      </div>
    </Modal>
  );
}
